import { useToast } from '@cad-arc';
import {
    CadSearchsaveResource,
    EventSearchForm,
    EventSearchGetResponse,
    FieldNameEnum,
    SavedEventSearchDto,
} from '@mark43/cad-api';
import { createApiErrorHandler, useApiClient } from '@m43/core';
import { QueryObserverResult, useMutation } from '@tanstack/react-query';
import { errorOccurredWhile } from '../common/helpers/error-occurred-while';
import { useBootstrapState } from '../bootstrap/bootstrap-provider';
import { formatEventSearchForm } from './format-event-search-form';
import { SavedSearch, SavedSearchDateRangeForm } from '../search-windows/saved-search';
import { useEnabledFeatures } from '../common/hooks/enabled-features/use-enabled-features';
import { SavedSearchHandler } from '../fr-page-layout/search-pages/types';
import { generateEnhancedSearchReport, prepareForSubmit } from './utils';
import { EventSearchFormType } from './types';
import { useBulkExportEventSearchResultsMutation } from './use-bulk-export-event-search-results-mutation';
import { useFullscreenModalControls } from '../modals/context/modal-context-provider';

export function SavedEventSearch({
    savedSearch,
    refetchSavedSearches,
    onSavedSearchClick,
}: Readonly<{
    savedSearch: SavedEventSearchDto;
    refetchSavedSearches: () => Promise<QueryObserverResult<EventSearchGetResponse | undefined>>;
    onSavedSearchClick: SavedSearchHandler<EventSearchForm>;
}>) {
    const { fieldDisplayNameByFieldName } = useBootstrapState();
    const renDisplayName =
        fieldDisplayNameByFieldName[FieldNameEnum.AGENCY_EVENT_REPORTING_EVENT_NUMBER.name];
    const apiClient = useApiClient();
    const toast = useToast();
    const { subpremiseSupport, enhancedSearchExportsEnabled } = useEnabledFeatures();

    const deleteSavedSearchMutation = useMutation((savedSearchId: number) => {
        return apiClient
            .request<CadSearchsaveResource.DeleteSavedEventSearch>({
                method: 'POST',
                url: 'cad_search/save/event/delete',
                data: { savedSearchId },
            })
            .catch(
                createApiErrorHandler(() => {
                    toast({
                        status: 'error',
                        description: errorOccurredWhile('deleting saved search'),
                    });
                })
            );
    });
    const updateSavedSearchNameMutation = useMutation(
        ({ savedSearchId, newName }: { savedSearchId: number; newName: string }) => {
            return apiClient
                .request<CadSearchsaveResource.UpdateEventSearchName>({
                    method: 'PUT',
                    url: 'cad_search/save/event/name',
                    data: { savedSearchId, newName },
                })
                .catch(
                    createApiErrorHandler(() => {
                        toast({
                            status: 'error',
                            description: errorOccurredWhile('updating saved search name'),
                        });
                    })
                );
        }
    );
    const bulkEventSearchExportMutation = useBulkExportEventSearchResultsMutation();
    const { open } = useFullscreenModalControls();

    const overrideDateRange = (
        eventSearchForm: EventSearchForm,
        dateRange?: SavedSearchDateRangeForm
    ) => {
        if (!dateRange) {
            return eventSearchForm;
        }

        const { startDate, endDate } = dateRange;

        return {
            ...eventSearchForm,
            dateRange: {
                start: startDate,
                end: endDate,
            },
        };
    };

    const onGenerateLog = (dateOverride?: SavedSearchDateRangeForm) => {
        generateEnhancedSearchReport({
            eventIds: [],
            open,
            bulkEventSearchExportMutation,
            eventSearchForm: prepareForSubmit(
                (overrideDateRange(
                    savedSearch.eventSearchForm,
                    dateOverride
                ) as unknown) as EventSearchFormType
            ),
        });
    };

    return (
        <SavedSearch
            savedSearchId={savedSearch.savedSearchId}
            savedSearchName={savedSearch.savedSearchName}
            lastExecutedDateUtc={savedSearch.lastExecutedDateUtc}
            formattedParams={formatEventSearchForm(
                savedSearch.eventSearchForm,
                renDisplayName,
                subpremiseSupport
            )}
            runSavedSearch={(dateRangeOverride) =>
                onSavedSearchClick({
                    search: savedSearch.eventSearchForm,
                    savedSearchId: savedSearch.savedSearchId,
                    dateRangeOverride,
                })
            }
            updateSavedSearchNameMutation={updateSavedSearchNameMutation}
            deleteSavedSearch={() =>
                deleteSavedSearchMutation.mutateAsync(savedSearch.savedSearchId)
            }
            refetchSavedSearches={refetchSavedSearches}
            shareEventSearchForm={savedSearch.shareEventSearchForm}
            onGenerateLog={enhancedSearchExportsEnabled ? onGenerateLog : undefined}
            showDateRangeOverride={enhancedSearchExportsEnabled}
        />
    );
}
